import {
  texto, identificador, numero, moneda, centro, totalizar, construirHojaTabla, descargarLibro, limpiarNombreArchivo,
} from "../../../../Components/ExcelTablaReporte";
import { fechaTxt } from "./aguinaldoUtils";

/**
 * Excel del aguinaldo (borrador o aplicado), con el estilo Biwo de ExcelTablaReporte. Usa los
 * MISMOS datos que el PDF (nomina/datosImpresionAguinaldo), así que los dos archivos no pueden
 * decir cosas distintas.
 *
 *  · Hoja "Detalle": un renglón por empleado con el cálculo completo (días del ejercicio,
 *    incidencias, días trabajados, % del año, días a pagar) y los importes. Autofiltro y TOTAL.
 *  · Hoja "Totales": un renglón por prenómina destino y el total general.
 */

const redondear = (n, d = 2) => Math.round((Number(n) || 0) * 10 ** d) / 10 ** d;
const sumar = (filas, campo, d = 2) => redondear(filas.reduce((acc, f) => acc + (Number(f[campo]) || 0), 0), d);
// ISR sin calcular: la celda queda vacía (no en $0.00, que se confundiría con "no retiene").
const monedaONada = (v) => moneda(v === null || v === undefined ? "" : v);

const descripcionDe = ({ proceso, aplicado, calculadoEn }) => [
  `Corte al ${fechaTxt(proceso.fechaCorte)}`,
  `Divisor ${proceso.diasAnio || 365} días`,
  proceso.tipoAplicacion === "extraordinaria" ? "Periodo extraordinario" : "Prenómina ordinaria",
  `Destino: ${proceso.destino || ""}`,
  proceso.incidencias ? `Incidencias descontadas: ${proceso.incidencias}` : "Sin descontar incidencias",
  aplicado ? "Aplicado" : `Borrador${calculadoEn ? " · ISR simulado" : " · ISR sin calcular"}`,
].join(" · ");

function hojaDetalle(datos) {
  const diasAnio = datos.proceso.diasAnio || 365;
  const columnas = [
    { titulo: "No.", tipo: "identificador", ancho: 8 },
    { titulo: "Empleado", tipo: "texto", ancho: 36 },
    { titulo: "Destino", tipo: "texto", ancho: 30 },
    { titulo: "Antigüedad (años)", tipo: "numero", ancho: 11 },
    { titulo: "Tabla de prestaciones", tipo: "texto", ancho: 22 },
    { titulo: "Días de aguinaldo", tipo: "numero", ancho: 11 },
    { titulo: "Días del ejercicio", tipo: "numero", ancho: 11 },
    { titulo: "Incidencias", tipo: "numero", ancho: 11 },
    { titulo: "Días trabajados", tipo: "numero", ancho: 11 },
    { titulo: `% del año (÷ ${diasAnio})`, tipo: "numero", ancho: 12 },
    { titulo: "Días a pagar", tipo: "numero", ancho: 12 },
    { titulo: "Salario diario", tipo: "moneda", ancho: 15 },
    { titulo: "Aguinaldo", tipo: "moneda", ancho: 16 },
    { titulo: "Importe manual", tipo: "centro", ancho: 10 },
    { titulo: "Exento ISR", tipo: "moneda", ancho: 15 },
    { titulo: "Gravado ISR", tipo: "moneda", ancho: 15 },
    { titulo: "ISR", tipo: "moneda", ancho: 14 },
    { titulo: "Neto", tipo: "moneda", ancho: 16 },
  ];

  const filas = (datos.grupos || []).flatMap((g) => (g.filas || []).map((f) => [
    identificador(f.codigo === null || f.codigo === undefined ? "" : String(f.codigo).padStart(4, "0")),
    texto(f.nombre),
    texto(g.titulo),
    numero(f.antiguedadAnios),
    texto(f.tablaPrestacion),
    numero(f.diasAguinaldo),
    numero(f.diasCalendario),
    numero(f.diasIncidencias || 0),
    numero(f.diasTrabajados),
    numero(redondear(((Number(f.diasTrabajados) || 0) / diasAnio) * 100, 2)),
    numero(redondear(f.diasProporcionales, 4)),
    moneda(f.salarioDiario),
    moneda(f.importe),
    centro(f.importeManual ? "Sí" : ""),
    monedaONada(f.exento),
    monedaONada(f.gravado),
    monedaONada(f.isr),
    monedaONada(f.neto),
  ]));

  // El salario diario es dinero pero no se suma: un total de salarios diarios no significa nada.
  const totales = totalizar(columnas, filas);
  totales[columnas.findIndex((c) => c.titulo === "Salario diario")] = null;

  return construirHojaTabla(
    { columnas, filas, totales },
    {
      titulo: `Aguinaldo ${datos.proceso.ejercicio}${datos.aplicado ? "" : " (borrador)"} · Detalle`,
      empresaNombre: datos.empresa?.nombre,
      descripcionFiltros: descripcionDe(datos),
    }
  );
}

function hojaTotales(datos) {
  const columnas = [
    { titulo: "Destino", tipo: "texto", ancho: 44 },
    { titulo: "Empleados", tipo: "numero", ancho: 12 },
    { titulo: "Días a pagar", tipo: "numero", ancho: 14 },
    { titulo: "Aguinaldo", tipo: "moneda", ancho: 17 },
    { titulo: "Exento ISR", tipo: "moneda", ancho: 17 },
    { titulo: "Gravado ISR", tipo: "moneda", ancho: 17 },
    { titulo: "ISR", tipo: "moneda", ancho: 16 },
    { titulo: "Neto", tipo: "moneda", ancho: 17 },
  ];

  const grupos = datos.grupos || [];
  const renglon = (titulo, filas, resalte) => [
    { ...texto(titulo), resalte },
    { ...numero(filas.length), resalte },
    { ...numero(sumar(filas, "diasProporcionales", 4)), resalte },
    { ...moneda(sumar(filas, "importe")), resalte },
    { ...moneda(sumar(filas, "exento")), resalte },
    { ...moneda(sumar(filas, "gravado")), resalte },
    { ...moneda(sumar(filas, "isr")), resalte },
    { ...moneda(sumar(filas, "neto")), resalte },
  ];

  // El total general va como renglón propio (y no con la fila TOTAL de la plantilla, que pinta
  // todo en formato de dinero): así empleados y días a pagar salen como números.
  const filas = [
    ...grupos.map((g) => renglon(g.titulo, g.filas || [])),
    renglon("TOTAL GENERAL", grupos.flatMap((g) => g.filas || []), "utilidad"),
  ];

  return construirHojaTabla(
    { columnas, filas, totales: [] },
    {
      titulo: `Aguinaldo ${datos.proceso.ejercicio}${datos.aplicado ? "" : " (borrador)"} · Totales`,
      empresaNombre: datos.empresa?.nombre,
      descripcionFiltros: descripcionDe(datos),
      sinAutofiltro: true,
      registros: grupos.length,
    }
  );
}

/** Descarga el libro con las dos hojas. `datos` = respuesta de nomina/datosImpresionAguinaldo. */
export const descargarExcelAguinaldo = (datos) => {
  const nombre = limpiarNombreArchivo(`Aguinaldo_${datos.proceso.ejercicio}${datos.aplicado ? "" : "_borrador"}`);
  descargarLibro(
    [
      { nombre: "Detalle", hoja: hojaDetalle(datos) },
      { nombre: "Totales", hoja: hojaTotales(datos) },
    ],
    nombre
  );
};

// Exportadas para probarlas sin descargar.
export { hojaDetalle, hojaTotales };
